import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPackage, FiTruck, FiCheckCircle, FiClock, FiX, FiAlertCircle, FiShoppingCart } from 'react-icons/fi';

const RecentOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/list-today-orders/?page=${currentPage}`);
        setOrders(response.data.orders || []); // Ensure orders is always an array
        setTotalPages(response.data.total_pages || 1);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, API_BASE_URL]);

  const statusStyles = {
    pending: {
      class: 'bg-yellow-100 text-yellow-800',
      icon: <FiClock className="mr-1" />,
      label: 'Pending'
    },
    processing: {
      class: 'bg-blue-100 text-blue-800',
      icon: <FiPackage className="mr-1" />,
      label: 'Processing'
    },
    shipped: {
      class: 'bg-purple-100 text-purple-800',
      icon: <FiTruck className="mr-1" />,
      label: 'Shipped'
    },
    delivered: {
      class: 'bg-green-100 text-green-800',
      icon: <FiCheckCircle className="mr-1" />,
      label: 'Delivered'
    },
    cancelled: {
      class: 'bg-red-100 text-red-800',
      icon: <FiX className="mr-1" />,
      label: 'Cancelled'
    },
    'out for delivery': {
      class: 'bg-indigo-100 text-indigo-800',
      icon: <FiTruck className="mr-1" />,
      label: 'Out for Delivery'
    }
  };

  const paymentStatusStyles = {
    paid: {
      class: 'bg-green-100 text-green-800',
      icon: <FiCheckCircle className="mr-1" />,
      label: 'Paid'
    },
    pending: {
      class: 'bg-yellow-100 text-yellow-800',
      icon: <FiClock className="mr-1" />,
      label: 'Pending'
    },
    failed: {
      class: 'bg-red-100 text-red-800',
      icon: <FiAlertCircle className="mr-1" />,
      label: 'Failed'
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const countItems = (cartItems) => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getOrderAmount = (orderSummary) => {
    // Use finalTotal if available (discounted orders), otherwise use total
    return orderSummary.finalTotal || orderSummary.total;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setLoading(true); // Show loading while fetching new page
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Orders</h2>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 border-b border-gray-200 flex items-center px-6">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 ml-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6 ml-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6 ml-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/6 ml-4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Orders</h2>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error loading orders: {error}</p>
              <button
                onClick={handleRetry}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Recent Orders</h2>
        {totalPages > 1 && orders.length > 0 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md text-sm ${
                currentPage === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md text-sm ${
                currentPage === totalPages 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {!orders || orders.length === 0 ? (
          <div className="p-12 text-center">
            <div className="flex flex-col items-center">
              <FiShoppingCart className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Today</h3>
              <p className="text-gray-500 mb-4">
                There are no orders for today yet. New orders will appear here when they come in.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-full inline-block align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{order.order_id?.split('-')[0] || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.billing_info?.fullName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.created_at ? formatDate(order.created_at) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.cart_items ? countItems(order.cart_items) : 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.order_summary ? (
                            <>
                              ₹{getOrderAmount(order.order_summary).toFixed(2)}
                              {order.order_summary?.discount && (
                                <span className="ml-1 text-xs text-green-600">
                                  (Saved ₹{order.order_summary.discount.toFixed(2)})
                                </span>
                              )}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                            statusStyles[order.status]?.class || 'bg-gray-100 text-gray-800'
                          }`}>
                            {statusStyles[order.status]?.icon || <FiClock className="mr-1" />}
                            {statusStyles[order.status]?.label || order.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                            paymentStatusStyles[order.payment_status]?.class || 'bg-gray-100 text-gray-800'
                          }`}>
                            {paymentStatusStyles[order.payment_status]?.icon || <FiClock className="mr-1" />}
                            {paymentStatusStyles[order.payment_status]?.label || order.payment_status || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentOrders;